// pages/opening-balance/OpeningBalanceEntry.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
    ChevronUp,
    Save,
    Edit,
    X,
    Calendar,
    Users,
    Truck,
    Briefcase,
    Package,
    HelpCircle
} from 'feather-icons-react/build/IconComponents';
import { bankActions } from '../../core/action/bankActions';
import { openingBalanceActions } from '../../core/action/openingBalanceActions';
import { customerActions } from '../../core/action/customerActions';
import { supplierActions } from '../../core/action/supplierAction';
import { productActions } from '../../core/action/productActions';

const MySwal = withReactContent(Swal);

const OpeningBalanceEntry = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [activeTab, setActiveTab] = useState('bank');
    const [loading, setLoading] = useState(false);
    const [financialYear, setFinancialYear] = useState('');
    const [entryDate, setEntryDate] = useState(new Date());

    // Bank Account State
    const [bankAccounts, setBankAccounts] = useState([]);
    const [selectedBank, setSelectedBank] = useState(null);
    const [bankOpeningBalance, setBankOpeningBalance] = useState('');

    // Customer State
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerBalance, setCustomerBalance] = useState('');
    const [customerBalanceType, setCustomerBalanceType] = useState('debit');

    // Supplier State
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [supplierBalance, setSupplierBalance] = useState('');
    const [supplierBalanceType, setSupplierBalanceType] = useState('credit');

    // Product State
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productQuantity, setProductQuantity] = useState('');
    const [productCost, setProductCost] = useState('');
    const [productSellingPrice, setProductSellingPrice] = useState('');

    // History state
    const [historyEntries, setHistoryEntries] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [editForm, setEditForm] = useState({
        opening_balance: '',
        balance_type: 'debit',
        entry_date: ''
    });

    // Financial Year Options
    const currentYear = new Date().getFullYear();
    const financialYearOptions = [
        { value: `${currentYear - 1}-${currentYear}`, label: `${currentYear - 1}-${currentYear}` },
        { value: `${currentYear}-${currentYear + 1}`, label: `${currentYear}-${currentYear + 1}` },
        { value: `${currentYear + 1}-${currentYear + 2}`, label: `${currentYear + 1}-${currentYear + 2}` }
    ];

    // Load initial data
    useEffect(() => {
        loadBankAccounts();
        loadCustomers();
        loadSuppliers();
        loadProducts();
        setFinancialYear(financialYearOptions[1].value); // Current financial year
    }, []);

    const getReferenceTypeForTab = useCallback((tab) => {
        const map = {
            bank: 'bank',
            customer: 'customer',
            supplier: 'supplier',
            product: 'product'
        };
        return map[tab] || 'bank';
    }, []);

    const loadOpeningHistory = useCallback(async () => {
        try {
            setHistoryLoading(true);
            const result = await openingBalanceActions.getHistory({
                reference_type: getReferenceTypeForTab(activeTab),
                financial_year: financialYear,
                limit: 100
            });

            if (result.success) {
                setHistoryEntries(result.data || []);
            } else {
                setHistoryEntries([]);
            }
        } catch (error) {
            console.error('Error loading opening balance history:', error);
            setHistoryEntries([]);
        } finally {
            setHistoryLoading(false);
        }
    }, [activeTab, financialYear, getReferenceTypeForTab]);

    useEffect(() => {
        if (financialYear) {
            loadOpeningHistory();
        }
    }, [financialYear, activeTab, loadOpeningHistory]);

    const loadBankAccounts = async () => {
        try {
            const result = await bankActions.getAccounts();
            if (result.success) {
                const accounts = result.data.map(acc => ({
                    value: acc.id,
                    label: `${acc.bank_name} - ${acc.account_name} (${acc.account_number})`,
                    currentBalance: acc.current_balance,
                    openingBalance: acc.opening_balance
                }));
                setBankAccounts(accounts);
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    const loadCustomers = async () => {
        try {
            const response = await customerActions.getCustomers({ page: 1, limit: 10000 });

            if (response.success) {
                const cust = response.data.map(c => ({
                    value: c.id,
                    label: `${c.name} (${c.phone || 'No Phone'})`
                }));
                setCustomers(cust);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };

    const loadSuppliers = async () => {
        try {
            const response = await supplierActions.getSuppliers({ page: 1, limit: 10000 });
            if (response.success) {
                const supp = response.data.map(s => ({
                    value: s.id,
                    label: `${s.name} (${s.phone || 'No Phone'})`
                }));
                setSuppliers(supp);
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await productActions.getProducts({ limit: 10000 });
            if (response.success) {
                const prods = response.data
                    .filter(p => (p.quantity || 0) > 0)
                    .map(p => ({
                        value: p.id,
                        label: `${p.product_name} (${p.sku})`,
                        currentStock: p.quantity || 0,
                        sellingPrice: p.selling_price || p.price || 0,
                        costPrice: p.cost_price || 0
                    }));
                setProducts(prods);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    const handleSaveBankOpening = async () => {
        if (!selectedBank) {
            MySwal.fire('Error', 'Please select a bank account', 'error');
            return;
        }
        if (!bankOpeningBalance || parseFloat(bankOpeningBalance) < 0) {
            MySwal.fire('Error', 'Please enter a valid opening balance', 'error');
            return;
        }

        setLoading(true);

        const data = {
            account_id: selectedBank.value,
            opening_balance: bankOpeningBalance,
            as_on_date: entryDate.toISOString().split('T')[0]
        };

        try {
            const result = await openingBalanceActions.saveBankOpening(data);
            if (result.success) {
                MySwal.fire('Success', 'Bank opening balance saved successfully', 'success');
                setSelectedBank(null);
                setBankOpeningBalance('');
                loadBankAccounts(); // Refresh list
                loadOpeningHistory();
            } else {
                MySwal.fire('Error', result.message, 'error');
            }
        } catch (error) {
            console.error('Error saving bank opening:', error);
            MySwal.fire('Error', 'Failed to save opening balance', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveCustomerOpening = async () => {
        if (!selectedCustomer) {
            MySwal.fire('Error', 'Please select a customer', 'error');
            return;
        }
        if (!customerBalance || parseFloat(customerBalance) <= 0) {
            MySwal.fire('Error', 'Please enter a valid balance', 'error');
            return;
        }

        setLoading(true);

        const data = {
            customer_id: selectedCustomer.value,
            opening_balance: customerBalance,
            balance_type: customerBalanceType,
            as_on_date: entryDate.toISOString().split('T')[0]
        };

        try {
            const result = await openingBalanceActions.saveCustomerOpening(data);
            if (result.success) {
                MySwal.fire('Success', 'Customer opening balance saved successfully', 'success');
                setSelectedCustomer(null);
                setCustomerBalance('');
                loadOpeningHistory();
            } else {
                MySwal.fire('Error', result.message, 'error');
            }
        } catch (error) {
            console.error('Error saving customer opening:', error);
            MySwal.fire('Error', 'Failed to save opening balance', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSupplierOpening = async () => {
        if (!selectedSupplier) {
            MySwal.fire('Error', 'Please select a supplier', 'error');
            return;
        }
        if (!supplierBalance || parseFloat(supplierBalance) <= 0) {
            MySwal.fire('Error', 'Please enter a valid balance', 'error');
            return;
        }

        setLoading(true);

        const data = {
            supplier_id: selectedSupplier.value,
            opening_balance: supplierBalance,
            balance_type: supplierBalanceType,
            as_on_date: entryDate.toISOString().split('T')[0]
        };

        try {
            const result = await openingBalanceActions.saveSupplierOpening(data);
            if (result.success) {
                MySwal.fire('Success', 'Supplier opening balance saved successfully', 'success');
                setSelectedSupplier(null);
                setSupplierBalance('');
                loadOpeningHistory();
            } else {
                MySwal.fire('Error', result.message, 'error');
            }
        } catch (error) {
            console.error('Error saving supplier opening:', error);
            MySwal.fire('Error', 'Failed to save opening balance', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProductOpening = async () => {
        if (!selectedProduct) {
            MySwal.fire('Error', 'Please select a product', 'error');
            return;
        }
        if (!productQuantity || parseFloat(productQuantity) <= 0) {
            MySwal.fire('Error', 'Please enter a valid quantity', 'error');
            return;
        }
        if (!productCost || parseFloat(productCost) <= 0) {
            MySwal.fire('Error', 'Please enter a valid cost price', 'error');
            return;
        }
        if (!productSellingPrice || parseFloat(productSellingPrice) <= 0) {
            MySwal.fire('Error', 'Please enter a valid selling price', 'error');
            return;
        }

        setLoading(true);

        const data = {
            product_id: selectedProduct.value,
            quantity: productQuantity,
            cost_price: productCost,
            selling_price: productSellingPrice,
            as_on_date: entryDate.toISOString().split('T')[0]
        };

        try {
            const result = await openingBalanceActions.saveProductOpening(data);
            if (result.success) {
                MySwal.fire('Success', 'Product opening stock saved successfully', 'success');
                setSelectedProduct(null);
                setProductQuantity('');
                setProductCost('');
                setProductSellingPrice('');
                loadProducts(); // Refresh list
                loadOpeningHistory();
            } else {
                MySwal.fire('Error', result.message, 'error');
            }
        } catch (error) {
            console.error('Error saving product opening:', error);
            MySwal.fire('Error', 'Failed to save opening stock', 'error');
        } finally {
            setLoading(false);
        }
    };

    const renderBankTab = () => (
        <div className="card">
            <div className="card-header">
                <h5 className="mb-0">Bank Account Opening Balance</h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-8">
                        <div className="mb-3">
                            <label className="form-label">Select Bank Account</label>
                            <Select
                                options={bankAccounts}
                                value={selectedBank}
                                onChange={setSelectedBank}
                                placeholder="Choose bank account..."
                                isClearable
                            />
                        </div>

                        {selectedBank && (
                            <div className="alert alert-info">
                                <small>Current Balance: {bankActions.formatCurrency(selectedBank.currentBalance || 0)}</small>
                                <br />
                                <small>Existing Opening: {bankActions.formatCurrency(selectedBank.openingBalance || 0)}</small>
                            </div>
                        )}

                        <div className="mb-3">
                            <label className="form-label">Opening Balance (Dr)</label>
                            <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                    type="number"

                                    className="form-control"
                                    value={bankOpeningBalance}
                                    onChange={(e) => setBankOpeningBalance(e.target.value)}
                                    placeholder="Enter opening balance"
                                />
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleSaveBankOpening}
                            disabled={loading}
                        >
                            <Save size={16} className="me-2" />
                            Save Bank Opening Balance
                        </button>
                    </div>

                    <div className="col-md-4">
                        <div className="card bg-light">
                            <div className="card-body">
                                <h6>Instructions</h6>
                                <small className="text-muted">
                                    Enter the opening balance for bank accounts as on the start of the financial year.
                                    This will update both opening_balance and current_balance fields.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCustomerTab = () => (
        <div className="card">
            <div className="card-header">
                <h5 className="mb-0">Customer Opening Balance (Accounts Receivable)</h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-8">
                        <div className="mb-3">
                            <label className="form-label">Select Customer</label>
                            <Select
                                options={customers}
                                value={selectedCustomer}
                                onChange={setSelectedCustomer}
                                placeholder="Choose customer..."
                                isClearable
                            />
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Opening Balance</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"

                                        className="form-control"
                                        value={customerBalance}
                                        onChange={(e) => setCustomerBalance(e.target.value)}
                                        placeholder="Enter balance"
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Balance Type</label>
                                <div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="customerBalanceType"
                                            id="customerDebit"
                                            value="debit"
                                            checked={customerBalanceType === 'debit'}
                                            onChange={(e) => setCustomerBalanceType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="customerDebit">
                                            Debit (Customer owes you)
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="customerBalanceType"
                                            id="customerCredit"
                                            value="credit"
                                            checked={customerBalanceType === 'credit'}
                                            onChange={(e) => setCustomerBalanceType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="customerCredit">
                                            Credit (You owe customer)
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleSaveCustomerOpening}
                            disabled={loading}
                        >
                            <Save size={16} className="me-2" />
                            Save Customer Opening Balance
                        </button>
                    </div>

                    <div className="col-md-4">
                        <div className="card bg-light">
                            <div className="card-body">
                                <h6>Instructions</h6>
                                <small className="text-muted">
                                    <strong>Debit:</strong> Customer owes you money (Accounts Receivable)
                                    <br />
                                    <strong>Credit:</strong> You owe customer money (advance payments)
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSupplierTab = () => (
        <div className="card">
            <div className="card-header">
                <h5 className="mb-0">Supplier Opening Balance (Accounts Payable)</h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-8">
                        <div className="mb-3">
                            <label className="form-label">Select Supplier</label>
                            <Select
                                options={suppliers}
                                value={selectedSupplier}
                                onChange={setSelectedSupplier}
                                placeholder="Choose supplier..."
                                isClearable
                            />
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Opening Balance</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"

                                        className="form-control"
                                        value={supplierBalance}
                                        onChange={(e) => setSupplierBalance(e.target.value)}
                                        placeholder="Enter balance"
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Balance Type</label>
                                <div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="supplierBalanceType"
                                            id="supplierCredit"
                                            value="credit"
                                            checked={supplierBalanceType === 'credit'}
                                            onChange={(e) => setSupplierBalanceType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="supplierCredit">
                                            Credit (You owe supplier)
                                        </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="supplierBalanceType"
                                            id="supplierDebit"
                                            value="debit"
                                            checked={supplierBalanceType === 'debit'}
                                            onChange={(e) => setSupplierBalanceType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="supplierDebit">
                                            Debit (Supplier owes you)
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleSaveSupplierOpening}
                            disabled={loading}
                        >
                            <Save size={16} className="me-2" />
                            Save Supplier Opening Balance
                        </button>
                    </div>

                    <div className="col-md-4">
                        <div className="card bg-light">
                            <div className="card-body">
                                <h6>Instructions</h6>
                                <small className="text-muted">
                                    <strong>Credit:</strong> You owe supplier money (Accounts Payable)
                                    <br />
                                    <strong>Debit:</strong> Supplier owes you money (advance payments)
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProductTab = () => (
        <div className="card">
            <div className="card-header">
                <h5 className="mb-0">Product Opening Stock</h5>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-8">
                        <div className="mb-3">
                            <label className="form-label">Select Product</label>
                            <Select
                                options={products}
                                value={selectedProduct}
                                onChange={setSelectedProduct}
                                placeholder="Choose product..."
                                isClearable
                            />
                        </div>

                        {selectedProduct && (
                            <div className="alert alert-info mb-3">
                                <div className="row">
                                    <div className="col-md-6">
                                        <small>Current Stock: {selectedProduct.currentStock || 0} units</small>
                                    </div>
                                    <div className="col-md-6">
                                        <small>Current Selling Price: ₹{selectedProduct.sellingPrice || 0}</small>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <small>Current Cost Price: ₹{selectedProduct.costPrice || 0}</small>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="row mb-3">
                            <div className="col-md-6">
                                <label className="form-label">Opening Quantity</label>
                                <input
                                    type="number"

                                    className="form-control"
                                    value={productQuantity}
                                    onChange={(e) => setProductQuantity(e.target.value)}
                                    placeholder="Enter quantity"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Cost Price per Unit (Purchase Price)</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"

                                        className="form-control"
                                        value={productCost}
                                        onChange={(e) => setProductCost(e.target.value)}
                                        placeholder="Enter cost price"
                                    />
                                </div>
                                <small className="text-muted">This is your purchase price</small>
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Selling Price per Unit (MRP/Retail Price)</label>
                            <div className="input-group">
                                <span className="input-group-text">₹</span>
                                <input
                                    type="number"

                                    className="form-control"
                                    value={productSellingPrice}
                                    onChange={(e) => setProductSellingPrice(e.target.value)}
                                    placeholder="Enter selling price"
                                />
                            </div>
                            <small className="text-muted">This will be displayed to customers</small>
                        </div>

                        {productCost && productSellingPrice && (
                            <div className="alert alert-info">
                                <div className="row">
                                    <div className="col-md-6">
                                        <strong>Profit Margin:</strong> ₹{(parseFloat(productSellingPrice) - parseFloat(productCost)).toFixed(2)}
                                    </div>
                                    <div className="col-md-6">
                                        <strong>Margin %:</strong> {((parseFloat(productSellingPrice) - parseFloat(productCost)) / parseFloat(productCost) * 100).toFixed(2)}%
                                    </div>
                                </div>
                            </div>
                        )}

                        {productQuantity && productCost && (
                            <div className="alert alert-success">
                                <strong>Total Stock Value:</strong> ₹{(parseFloat(productQuantity) * parseFloat(productCost)).toFixed(2)}
                            </div>
                        )}

                        <button
                            className="btn btn-primary"
                            onClick={handleSaveProductOpening}
                            disabled={loading}
                        >
                            <Save size={16} className="me-2" />
                            Save Opening Stock
                        </button>
                    </div>

                    <div className="col-md-4">
                        <div className="card bg-light">
                            <div className="card-body">
                                <h6>Instructions</h6>
                                <small className="text-muted">
                                    <strong>Cost Price:</strong> The price you paid to purchase the product<br />
                                    <strong>Selling Price:</strong> The price at which you will sell to customers<br />
                                    <strong>Profit Margin:</strong> The difference between selling price and cost price<br />
                                    This will update inventory and create an inventory log.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);

    const formatCurrency = useCallback((amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(parseFloat(amount || 0));
    }, []);

    const handleStartEditHistory = useCallback((entry) => {
        setEditingEntry(entry);
        setEditForm({
            opening_balance: entry.opening_balance,
            balance_type: entry.balance_type || 'debit',
            entry_date: entry.entry_date || new Date().toISOString().split('T')[0]
        });
    }, []);

    const handleCancelEditHistory = useCallback(() => {
        setEditingEntry(null);
        setEditForm({
            opening_balance: '',
            balance_type: 'debit',
            entry_date: ''
        });
    }, []);

    const handleUpdateHistory = useCallback(async () => {
        if (!editingEntry?.id) {
            return;
        }

        if (parseFloat(editForm.opening_balance || 0) < 0) {
            MySwal.fire('Error', 'Opening balance cannot be negative', 'error');
            return;
        }

        const result = await openingBalanceActions.updateHistoryEntry(editingEntry.id, {
            opening_balance: parseFloat(editForm.opening_balance || 0),
            balance_type: editForm.balance_type,
            entry_date: editForm.entry_date
        });

        if (result.success) {
            MySwal.fire('Success', result.message || 'Opening balance updated successfully', 'success');
            handleCancelEditHistory();
            loadOpeningHistory();
            if (activeTab === 'bank') {
                loadBankAccounts();
            }
        } else {
            MySwal.fire('Error', result.message || 'Failed to update opening balance', 'error');
        }
    }, [editingEntry, editForm, handleCancelEditHistory, loadOpeningHistory, activeTab]);

    const renderHistorySection = () => (
        <div className="card mt-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Opening Balance History</h5>
                <small className="text-muted">{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} entries</small>
            </div>
            <div className="card-body">
                {editingEntry && (
                    <div className="border rounded p-3 mb-3 bg-light">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label">Opening Balance</label>
                                <div className="input-group">
                                    <span className="input-group-text">₹</span>
                                    <input
                                        type="number"
                                        className="form-control"
                                        min="0"
                                        step="0.01"
                                        value={editForm.opening_balance}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, opening_balance: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Balance Type</label>
                                <select
                                    className="form-select"
                                    value={editForm.balance_type}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, balance_type: e.target.value }))}
                                    disabled={activeTab === 'bank'}
                                >
                                    <option value="debit">Debit</option>
                                    <option value="credit">Credit</option>
                                </select>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Entry Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={editForm.entry_date}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, entry_date: e.target.value }))}
                                />
                            </div>
                            <div className="col-md-2 d-flex gap-2">
                                <button className="btn btn-primary w-100" onClick={handleUpdateHistory}>
                                    <Save size={14} className="me-1" />
                                    Update
                                </button>
                                <button className="btn btn-secondary" onClick={handleCancelEditHistory}>
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                        {activeTab === 'product' && (
                            <small className="text-danger mt-2 d-block">Product opening history cannot be edited from this section.</small>
                        )}
                    </div>
                )}

                <div className="table-responsive">
                    <table className="table table-bordered align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Reference</th>
                                <th>Balance Type</th>
                                <th className="text-end">Opening Balance</th>
                                <th className="text-end">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {historyLoading && (
                                <tr>
                                    <td colSpan="5" className="text-center py-4">Loading history...</td>
                                </tr>
                            )}
                            {!historyLoading && historyEntries.length === 0 && (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 text-muted">No opening balance history found for this tab.</td>
                                </tr>
                            )}
                            {!historyLoading && historyEntries.map((entry) => (
                                <tr key={entry.id}>
                                    <td>{entry.entry_date}</td>
                                    <td>{entry.reference_name || `Ref #${entry.reference_id}`}</td>
                                    <td>
                                        <span className={`badge ${entry.balance_type === 'credit' ? 'bg-warning text-dark' : 'bg-info text-dark'}`}>
                                            {(entry.balance_type || 'debit').toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="text-end">{formatCurrency(entry.opening_balance)}</td>
                                    <td className="text-end">
                                        <button
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleStartEditHistory(entry)}
                                            disabled={entry.reference_type === 'product'}
                                            title={entry.reference_type === 'product' ? 'Product opening history cannot be edited here' : 'Edit'}
                                        >
                                            <Edit size={14} className="me-1" />
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Opening Balance Entry</h4>
                            <h6>Enter opening balances for the financial year</h6>
                        </div>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <div className="d-flex align-items-center">
                                <Calendar size={16} className="me-2 text-muted" />
                                <DatePicker
                                    selected={entryDate}
                                    onChange={setEntryDate}
                                    dateFormat="dd/MM/yyyy"
                                    className="form-control form-control-sm"
                                    maxDate={new Date()}
                                />
                            </div>
                        </li>
                        <li>
                            <Select
                                className="react-select"
                                classNamePrefix="select"
                                options={financialYearOptions}
                                value={financialYearOptions.find(opt => opt.value === financialYear)}
                                onChange={(opt) => setFinancialYear(opt?.value || '')}
                                placeholder="Financial Year"
                                isClearable={false}
                                styles={{
                                    control: (base) => ({ ...base, minWidth: '150px' })
                                }}
                            />
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Help')}>
                                <button className="btn btn-outline-info">
                                    <HelpCircle size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Info Card */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Financial Year:</strong> {financialYear}
                                    </div>
                                    <div>
                                        <strong>Opening Date:</strong> {entryDate.toLocaleDateString('en-IN')}
                                    </div>
                                    <div>
                                        <small className="text-muted">
                                            Enter opening balances before starting transactions
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'bank' ? 'active' : ''}`}
                            onClick={() => setActiveTab('bank')}
                        >
                            <Briefcase size={16} className="me-2" />
                            Bank Accounts
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'customer' ? 'active' : ''}`}
                            onClick={() => setActiveTab('customer')}
                        >
                            <Users size={16} className="me-2" />
                            Customers
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'supplier' ? 'active' : ''}`}
                            onClick={() => setActiveTab('supplier')}
                        >
                            <Truck size={16} className="me-2" />
                            Suppliers
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'product' ? 'active' : ''}`}
                            onClick={() => setActiveTab('product')}
                        >
                            <Package size={16} className="me-2" />
                            Products (Inventory)
                        </button>
                    </li>
                </ul>

                {/* Tab Content */}
                {activeTab === 'bank' && renderBankTab()}
                {activeTab === 'customer' && renderCustomerTab()}
                {activeTab === 'supplier' && renderSupplierTab()}
                {activeTab === 'product' && renderProductTab()}

                {renderHistorySection()}
            </div>
        </div>
    );
};

export default OpeningBalanceEntry;